import {agentCatalog} from './catalog';
import type {AgentDraft, AgentMessage, AgentMode, AgentOperation, AgentProgress, AgentReply} from './types';

/**
 * `POST /v3/marketing/automation-agent/run` streams SSE: `progress` events, then exactly one `reply` or `error`.
 * Uses fetch because the shared axios instance buffers the body and rewrites dates. Never saves or runs a flow.
 */

const AGENT_UNAVAILABLE_MESSAGE = 'The automation agent API is unavailable. Start or restart the backend with the agent endpoint, then retry.';
const AGENT_ENDED_EARLY_MESSAGE = 'The agent’s response ended early. Your flow has not changed.';
const AGENT_INCOMPLETE_MESSAGE = 'The agent returned an incomplete answer. Your flow has not changed.';
const AGENT_FAILED_MESSAGE = 'The agent could not respond. Try again.';
const AGENT_GATEWAY_MESSAGE = 'The agent could not complete its response. Your image is still attached; try again.';

export interface SseEvent {
    event: string;
    data: string;
}

export interface SseParserState {
    /** The unterminated tail of the last chunk. */
    buffer: string;
    event: string;
    data: string[];
}

export const createSseState = (): SseParserState => ({buffer: '', event: '', data: []});

/**
 * Returns the events each chunk completes; a partial line, or a `\r` that may be half of `\r\n`, waits
 * for the next chunk. Comments are dropped, multi-line data joins with `\n`, `id:`/`retry:` are ignored.
 */
export const parseSse = (chunk: string, state: SseParserState): SseEvent[] => {
    const events: SseEvent[] = [];
    let text = state.buffer + chunk;
    let held = '';
    if (text.endsWith('\r')) {
        held = '\r';
        text = text.slice(0, -1);
    }
    const lines = text.split(/\r\n|\r|\n/);
    state.buffer = lines.pop()! + held;
    for (const line of lines) {
        if (!line) {
            if (state.data.length) events.push({event: state.event || 'message', data: state.data.join('\n')});
            state.event = '';
            state.data = [];
            continue;
        }
        if (line.startsWith(':')) continue;
        const separator = line.indexOf(':');
        const field = separator === -1 ? line : line.slice(0, separator);
        let value = separator === -1 ? '' : line.slice(separator + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        if (field === 'event') state.event = value;
        else if (field === 'data') state.data.push(value);
    }
    return events;
};

/** Completes whatever the stream left unterminated when it closed. */
export const flushSse = (state: SseParserState): SseEvent[] => parseSse('\n\n', state);

const OPERATION_KINDS = ['add', 'update', 'remove', 'connect', 'disconnect'];

const isPlainObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

const parseJson = (data: string): unknown => {
    try {
        return JSON.parse(data);
    } catch {
        return undefined;
    }
};

/** The API's serializer sends absent optional fields as `null`. */
const normalizeOperation = (operation: unknown): unknown => isPlainObject(operation)
    ? Object.fromEntries(Object.entries(operation).filter(([, value]) => value != null))
    : operation;

const isOperation = (operation: unknown): operation is AgentOperation => isPlainObject(operation)
    && OPERATION_KINDS.includes(operation.kind as string)
    && Object.entries(operation).every(([key, value]) => key === 'config' ? isPlainObject(value) : typeof value === 'string');

const validateReply = (data: unknown): AgentReply => {
    const reply = data as AgentReply;
    if (!isPlainObject(data) || typeof reply.message !== 'string' || !Array.isArray(reply.operations) || reply.operations.length > 100
        || !Array.isArray(reply.questions) || reply.questions.length > 3 || !Array.isArray(reply.notes)
        || reply.notes.some(note => typeof note !== 'string')
        || reply.questions.some(question => !question || typeof question.question !== 'string' || !Array.isArray(question.options)
            || question.options.some(option => typeof option !== 'string'))) {
        throw new Error(AGENT_INCOMPLETE_MESSAGE);
    }
    const operations = reply.operations.map(normalizeOperation);
    if (!operations.every(isOperation)) throw new Error(AGENT_INCOMPLETE_MESSAGE);
    return {message: reply.message, questions: reply.questions, operations, notes: reply.notes,
        ...(isPlainObject(reply.usage) ? {usage: reply.usage} : {})};
};

const isProgress = (data: unknown): data is AgentProgress => isPlainObject(data) && typeof data.label === 'string';

export interface RunAgentOptions {
    mode: AgentMode;
    signal: AbortSignal;
    onProgress?: (progress: AgentProgress) => void;
}

/** `response.body.getReader()`, or a test's stand-in. */
interface StreamReader {
    read(): Promise<{done: boolean; value?: Uint8Array | string}>;
    cancel(): Promise<void>;
}

const serializeMessages = (messages: AgentMessage[]) => messages.map(message => ({
    role: message.role,
    // The assistant's questions, notes and applied summary are part of what it said.
    text: [message.text, ...(message.questions || []).map(question => question.question), ...(message.notes || []), ...(message.applied || [])].join('\n'),
    images: (message.images || []).map(({name, mimeType, data}) => ({name, mimeType, data})),
    hidden: !!message.hidden,
}));

const failureMessage = async (response: Response): Promise<string> => {
    if (response.status === 404) return AGENT_UNAVAILABLE_MESSAGE;
    let message: unknown;
    try {
        const body = parseJson(await response.text());
        if (isPlainObject(body)) message = body.message ?? body.detail;
    } catch {
        // No readable body: fall through to the status-based message.
    }
    if (typeof message === 'string' && message.trim()) return message;
    return response.status === 502 ? AGENT_GATEWAY_MESSAGE : AGENT_FAILED_MESSAGE;
};

const readReply = async (response: Response, onProgress?: (progress: AgentProgress) => void): Promise<AgentReply> => {
    const state = createSseState();
    let reply: AgentReply | undefined;
    const handle = (event: SseEvent) => {
        if (event.event === 'progress') {
            const progress = parseJson(event.data);
            if (isProgress(progress)) onProgress?.({step: Number(progress.step) || 0, tool: String(progress.tool ?? ''), label: progress.label});
        } else if (event.event === 'reply') {
            reply = validateReply(parseJson(event.data));
        } else if (event.event === 'error') {
            const failure = parseJson(event.data);
            throw new Error(isPlainObject(failure) && typeof failure.message === 'string' && failure.message ? failure.message : AGENT_FAILED_MESSAGE);
        }
    };
    const consume = (chunk: string) => {
        for (const event of parseSse(chunk, state)) {
            handle(event);
            if (reply) return;
        }
    };

    if (!response.body) {
        consume(await response.text());
    } else {
        const reader: StreamReader = response.body.getReader();
        let decoder: TextDecoder | undefined;
        try {
            while (!reply) {
                const {done, value} = await reader.read();
                if (done) break;
                if (value === undefined) continue;
                if (typeof value === 'string') {
                    consume(value);
                } else {
                    decoder = decoder || new TextDecoder();
                    consume(decoder.decode(value, {stream: true}));
                }
            }
        } finally {
            reader.cancel().catch(() => undefined);
        }
    }
    if (!reply) for (const event of flushSse(state)) {handle(event); if (reply) break;}
    if (!reply) throw new Error(AGENT_ENDED_EARLY_MESSAGE);
    return reply;
};

/** One agent turn. Rejects with a panel-ready message on HTTP failure, an `error` event, a malformed reply or an early close. */
export const runAutomationAgent = async (messages: AgentMessage[], draft: AgentDraft, {mode, signal, onProgress}: RunAgentOptions): Promise<AgentReply> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v3/marketing/automation-agent/run`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? {Authorization: token} : {})},
        body: JSON.stringify({
            messages: serializeMessages(messages),
            draft: {...draft, currentTime: new Date().toISOString(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone},
            catalog: agentCatalog(),
            mode,
        }),
        signal,
    });
    if (!response.ok) throw new Error(await failureMessage(response));
    return readReply(response, onProgress);
};
